import * as THREE from 'three';
import { Character } from './characters';

/**
 * Fauna CC0 de Quaternius (paquete Animated Animal Pack + Stag + Peces).
 * Cada modelo trae su propio esqueleto:
 *   - Animales terrestres → `AnimalArmature|Idle|Walk|Gallop|...`
 *   - Peces/marinos      → `Fish_Armature|Swimming_Normal|...`
 */

export interface FaunaSpec {
  url: string;
  height: number;
  spin: number;
  idle: string;
  walk: string;
  homeOffset: number;
  category: 'land' | 'water';
}

export const FAUNA: FaunaSpec[] = [
  // Animales terrestres.
  { url: '/assets/polypizza/cow.glb',     height: 1.15, spin: 0.2,  idle: 'AnimalArmature|Idle', walk: 'AnimalArmature|Walk', homeOffset: 4.5, category: 'land' },
  { url: '/assets/polypizza/donkey.glb',  height: 1.30, spin: -0.4, idle: 'AnimalArmature|Idle', walk: 'AnimalArmature|Walk', homeOffset: 5.0, category: 'land' },
  { url: '/assets/polypizza/deer.glb',    height: 1.30, spin: 0.5,  idle: 'AnimalArmature|Idle', walk: 'AnimalArmature|Walk', homeOffset: 5.0, category: 'land' },
  { url: '/assets/polypizza/alpaca.glb',  height: 1.25, spin: -0.2, idle: 'AnimalArmature|Idle', walk: 'AnimalArmature|Walk', homeOffset: 4.5, category: 'land' },
  { url: '/assets/polypizza/bull.glb',    height: 1.35, spin: 0.7,  idle: 'AnimalArmature|Idle', walk: 'AnimalArmature|Walk', homeOffset: 4.5, category: 'land' },
  { url: '/assets/polypizza/fox.glb',     height: 0.65, spin: 0.3,  idle: 'AnimalArmature|Idle', walk: 'AnimalArmature|Walk', homeOffset: 3.0, category: 'land' },
  { url: '/assets/polypizza/shiba.glb',   height: 0.55, spin: -0.6, idle: 'AnimalArmature|Idle', walk: 'AnimalArmature|Walk', homeOffset: 3.0, category: 'land' },
  { url: '/assets/polypizza/stag.glb',    height: 1.40, spin: 0.9,  idle: 'AnimalArmature|Idle', walk: 'AnimalArmature|Walk', homeOffset: 5.0, category: 'land' },
  { url: '/assets/polypizza/husky.glb',   height: 0.85, spin: -0.3, idle: 'AnimalArmature|Idle', walk: 'AnimalArmature|Walk', homeOffset: 3.5, category: 'land' },
  { url: '/assets/polypizza/wolf.glb',    height: 0.95, spin: 0.4,  idle: 'AnimalArmature|Idle', walk: 'AnimalArmature|Walk', homeOffset: 4.0, category: 'land' },
  { url: '/assets/polypizza/horse.glb',   height: 1.55, spin: -0.8, idle: 'AnimalArmature|Idle', walk: 'AnimalArmature|Walk', homeOffset: 5.5, category: 'land' },
  { url: '/assets/polypizza/horseW.glb',  height: 1.55, spin: 1.1,  idle: 'AnimalArmature|Idle', walk: 'AnimalArmature|Walk', homeOffset: 5.5, category: 'land' },
  // Peces (categoría agua; nadan en círculos sin tocar el suelo).
  { url: '/assets/polypizza/clownfish.glb', height: 0.32, spin: 0.0, idle: 'Fish_Armature|Swimming_Normal', walk: 'Fish_Armature|Swimming_Fast', homeOffset: 2.0, category: 'water' },
  { url: '/assets/polypizza/koi.glb',       height: 0.45, spin: 0.3, idle: 'Fish_Armature|Swimming_Normal', walk: 'Fish_Armature|Swimming_Fast', homeOffset: 2.5, category: 'water' },
  { url: '/assets/polypizza/swordfish.glb', height: 0.55, spin: -0.4, idle: 'Fish_Armature|Swimming_Normal', walk: 'Fish_Armature|Swimming_Fast', homeOffset: 2.5, category: 'water' }
];

interface FaunaInstance {
  spec: FaunaSpec;
  char: Character;
  home: THREE.Vector3;
  phase: number;
  nextClipChange: number;
  activeClip: string;
  dir: THREE.Vector3;
  until: number;
  yBase: number;
}

export type { FaunaInstance };

function shuffle<T>(arr: T[], rng: () => number): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Spawnea fauna cerca del origen respetando si el OA es 'water' (peces) o no. */
export async function spawnFauna(
  landCount: number,
  waterCount: number,
  scene: THREE.Scene,
  origin: THREE.Vector3,
  rng: () => number = Math.random
): Promise<FaunaInstance[]> {
  const out: FaunaInstance[] = [];
  const landPool = shuffle(FAUNA.filter((f) => f.category === 'land'), rng);
  const waterPool = shuffle(FAUNA.filter((f) => f.category === 'water'), rng);
  for (let i = 0; i < Math.min(landCount, landPool.length); i++) {
    const spec = landPool[i];
    out.push(await spawnOne(spec, scene, origin, rng, /*yBase*/0));
  }
  for (let i = 0; i < Math.min(waterCount, waterPool.length); i++) {
    const spec = waterPool[i];
    out.push(await spawnOne(spec, scene, origin, rng, /*yBase*/1.1 + i * 0.6));
  }
  return out;
}

async function spawnOne(spec: FaunaSpec, scene: THREE.Scene, origin: THREE.Vector3, rng: () => number, yBase: number): Promise<FaunaInstance> {
  const char = await Character.load(spec.url, { height: spec.height, spin: spec.spin });
  const angle = rng() * Math.PI * 2;
  const r = spec.homeOffset + rng() * 3;
  const home = new THREE.Vector3(origin.x + Math.cos(angle) * r, yBase, origin.z + Math.sin(angle) * r);
  char.group.position.copy(home);
  char.group.rotation.y = rng() * Math.PI * 2;
  scene.add(char.group);
  char.play(spec.idle);
  return {
    spec,
    char,
    home,
    phase: rng() * Math.PI * 2,
    nextClipChange: 2 + rng() * 4,
    activeClip: spec.idle,
    dir: new THREE.Vector3(Math.cos(angle), 0, Math.sin(angle)),
    until: 0,
    yBase
  };
}

export function updateFauna(insts: FaunaInstance[], dt: number, elapsedSec: number): void {
  for (const fa of insts) {
    fa.nextClipChange -= dt;
    if (fa.nextClipChange <= 0) {
      const wantsWalk = Math.random() < 0.4 && fa.activeClip !== fa.spec.walk;
      const wantsIdle = Math.random() < 0.6 && fa.activeClip !== fa.spec.idle;
      if (wantsWalk) {
        const angle = Math.random() * Math.PI * 2;
        fa.dir.set(Math.cos(angle), 0, Math.sin(angle));
        fa.until = elapsedSec + 2 + Math.random() * 3;
        fa.char.play(fa.spec.walk);
        fa.activeClip = fa.spec.walk;
      } else if (wantsIdle) {
        fa.char.play(fa.spec.idle);
        fa.activeClip = fa.spec.idle;
      }
      fa.nextClipChange = 3 + Math.random() * 4;
    }
    if (fa.activeClip === fa.spec.walk) {
      const next = fa.char.group.position.clone().addScaledVector(fa.dir, dt * 0.9);
      const dxHome = next.x - fa.home.x;
      const dzHome = next.z - fa.home.z;
      if (Math.hypot(dxHome, dzHome) > 3 || elapsedSec > fa.until) {
        fa.char.play(fa.spec.idle);
        fa.activeClip = fa.spec.idle;
        fa.dir.set(-fa.dir.x, 0, -fa.dir.z);
      } else {
        fa.char.group.position.copy(next);
        fa.char.group.rotation.y = Math.atan2(fa.dir.x, fa.dir.z);
      }
    } else {
      // Pequeño cabeceo para que no parezca congelado.
      fa.char.group.position.y = fa.yBase + Math.sin(elapsedSec * 0.6 + fa.phase) * 0.06;
      if (fa.spec.category === 'water') {
        // Los peces se balancean suavemente sobre su casa.
        fa.char.group.position.x = fa.home.x + Math.sin(elapsedSec * 0.4 + fa.phase) * 0.4;
        fa.char.group.position.z = fa.home.z + Math.cos(elapsedSec * 0.4 + fa.phase) * 0.4;
      }
    }
    fa.char.update(dt);
  }
}
